import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Inbox, Loader2, RefreshCw, MessageCircle, Lock, Clock, AlertCircle, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ReservationChatDialog } from "./EmployerApplications";

/**
 * Unified Employer Inbox
 * ----------------------
 * One flat feed of every reservation with a chat thread. Aggregates state
 * (expires_at, auto_extended, expired) + last-message preview + unread count
 * so recruiters can triage without opening each application.
 */
const EmployerInbox = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openChat, setOpenChat] = useState(null); // reservation-shaped payload for ReservationChatDialog

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employer/inbox");
            setRows(data.results || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const totalUnread = useMemo(() => rows.reduce((n, r) => n + (r.unread_count || 0), 0), [rows]);

    const openThread = (row) => {
        setOpenChat({
            id: row.reservation_id,
            application_id: row.application_id,
            label: new Date(row.slot_iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
        });
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Inbox size={18} /> <p className="overline">Reservation inbox</p>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mt-1">
                        Chat &amp; hold status{totalUnread > 0 && <span data-testid="inbox-unread-total" className="ml-2 text-emerald-600">· {totalUnread} unread</span>}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">Every candidate you&rsquo;ve reserved a slot for, sorted by newest activity.</p>
                </div>
                <button onClick={load} disabled={loading} data-testid="inbox-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div data-testid="inbox-empty" className="card-soft p-10 text-center">
                    <Inbox size={28} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-foreground font-medium">No reservation threads yet.</p>
                    <p className="mt-1 text-xs text-muted-foreground">Reserve a slot from any candidate&rsquo;s Smart Reply modal and their chat will appear here.</p>
                    <Link to="/employer/applications" className="mt-4 inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 hover:underline">
                        Open applications <ArrowRight size={11} />
                    </Link>
                </div>
            ) : (
                <div className="space-y-2" data-testid="inbox-list">
                    {rows.map((r) => <InboxRow key={r.reservation_id} row={r} onOpen={openThread} />)}
                </div>
            )}

            {openChat && (
                <ReservationChatDialog
                    reservation={openChat}
                    onClose={() => { setOpenChat(null); load(); }}
                />
            )}
        </div>
    );
};

const InboxRow = ({ row, onOpen }) => {
    const slotLabel = new Date(row.slot_iso).toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
    const stateBadge = row.expired
        ? { text: "Expired", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" }
        : row.auto_extended
            ? { text: "Auto-extended", cls: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300" }
            : { text: "Active hold", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" };
    return (
        <motion.button
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            type="button"
            onClick={() => onOpen(row)}
            data-testid={`inbox-row-${row.reservation_id}`}
            className="w-full text-left card-soft p-4 flex items-start gap-3 hover:ring-2 hover:ring-emerald-500/30 transition-shadow"
        >
            <div className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center font-heading font-semibold text-sm">
                {(row.candidate_name || row.candidate_email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-foreground truncate">{row.candidate_name || row.candidate_email}</div>
                    <span data-testid={`inbox-state-${row.reservation_id}`} className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${stateBadge.cls}`}>
                        {stateBadge.text}
                    </span>
                    {row.unread_count > 0 && (
                        <span data-testid={`inbox-unread-${row.reservation_id}`} className="inline-flex items-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-400 px-1.5 py-0.5 text-[10px] font-semibold">
                            <AlertCircle size={9} className="mr-0.5" /> {row.unread_count} new
                        </span>
                    )}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                    <span className="inline-flex items-center gap-1"><Lock size={9} />{slotLabel}</span>
                    {row.job_title && <><span>·</span><span className="truncate">{row.job_title}</span></>}
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle size={9} />{row.message_count} message{row.message_count === 1 ? "" : "s"}</span>
                </div>
                {row.last_message_preview && (
                    <div className={`mt-2 text-xs italic truncate ${row.unread_count > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        <span className="not-italic text-[10px] uppercase tracking-wider mr-1 opacity-70">{row.last_message_sender === "candidate" ? "Them:" : "You:"}</span>
                        {row.last_message_preview}
                    </div>
                )}
                {row.last_message_at && (
                    <div className="mt-1 text-[10px] text-muted-foreground inline-flex items-center gap-1">
                        <Clock size={9} /> {new Date(row.last_message_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                )}
            </div>
            <ArrowRight size={14} className="text-muted-foreground shrink-0 mt-2" />
        </motion.button>
    );
};

export default EmployerInbox;
